import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { listJobs } from '../features/contentCompliance/contentComplianceSlice';
import { Plus, Clock, Coins } from 'lucide-react';
import youtubeIcon from '../assets/youtube_icon.png';
import tiktokIcon from '../assets/tiktok_icon.png';
import instagramIcon from '../assets/insta_icon.png';
import './RecentActivity.css';

// Facebook icon as inline SVG data URI (no PNG asset available)
const facebookIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>')}`;

function RecentActivity({ limit = 5, onStartNewScan }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jobsList = useSelector((state) => state.contentCompliance.jobsList);
  const jobsListStatus = useSelector((state) => state.contentCompliance.jobsListStatus);

  // Fetch recent jobs on mount
  useEffect(() => {
    if (jobsListStatus === 'idle') {
      dispatch(listJobs({ limit, sortOrder: 'desc' }));
    }
  }, [dispatch, jobsListStatus, limit]);

  // Format date from ISO string
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      let normalizedDateString = dateString.trim();
      
      if (normalizedDateString.includes('T') && 
          !normalizedDateString.endsWith('Z') && 
          !normalizedDateString.match(/[+-]\d{2}:?\d{2}$/)) {
        normalizedDateString = normalizedDateString + 'Z';
      }
      
      const date = new Date(normalizedDateString);
      
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesStr = minutes < 10 ? '0' + minutes : minutes;
      
      return `${day} ${month} ${year}, ${hours}:${minutesStr} ${ampm}`;
    } catch (error) {
      return '';
    }
  };

  // Get platform icon
  const getPlatformIcon = (platform) => {
    const platformLower = platform?.toLowerCase();
    if (platformLower === 'youtube') return youtubeIcon;
    if (platformLower === 'tiktok') return tiktokIcon;
    if (platformLower === 'instagram') return instagramIcon;
    if (platformLower === 'facebook') return facebookIcon;
    return youtubeIcon; // Default
  };

  // Transform job data to recent activity format (limit to specified number)
  const recentActivity = (jobsList || []).slice(0, limit).map((job) => ({
    id: job.job_id,
    title: job.title || 'Untitled',
    date: formatDate(job.created_at || job.updated_at),
    score: job.data?.overall_score || null,
    platform: job.platform || 'youtube',
    // null = predates credit tracking, or enforcement was off at scan time.
    creditsCharged: job.credits_charged ?? null
  }));

  const handleItemClick = (item) => {
    navigate(`/dashboard/history?job=${item.id}`);
  };

  const handleStartNewScan = () => {
    if (onStartNewScan) {
      onStartNewScan();
    } else {
      navigate('/dashboard/new-check');
    }
  };

  return (
    <section className="recent-activity">
      <h3 className="recent-activity-title">Recent Activity</h3>
      {jobsListStatus === 'loading' && recentActivity.length === 0 ? (
        <div className="recent-activity-loading">
          <p>Loading recent activity...</p>
        </div>
      ) : recentActivity.length === 0 ? (
        <div className="recent-activity-empty">
          <Clock size={48} className="recent-activity-empty-icon" />
          <p className="recent-activity-empty-text">No activity yet</p>
          <p className="recent-activity-empty-subtext">Start by adding your first piece of content</p>
          <button className="recent-activity-empty-button" onClick={handleStartNewScan}>
            <Plus size={18} />
            Check Content
          </button>
        </div>
      ) : (
        <div className="recent-activity-list">
          {recentActivity.map((item) => (
            <div 
              key={item.id} 
              className="recent-activity-item"
              onClick={() => handleItemClick(item)}
            >
              <div className="recent-activity-item-icon">
                <img 
                  src={getPlatformIcon(item.platform)} 
                  alt={item.platform} 
                  className="recent-activity-platform-icon" 
                />
              </div>
              <div className="recent-activity-item-content">
                <h4 className="recent-activity-item-title">{item.title}</h4>
                <div className="recent-activity-item-meta">
                  <p className="recent-activity-item-date">{item.date}</p>
                  {item.creditsCharged !== null && (
                    <span className="recent-activity-item-credits">
                      <Coins size={12} className="recent-activity-credits-icon" aria-hidden="true" />
                      {item.creditsCharged > 0 ? `${item.creditsCharged} credits` : 'Free'}
                    </span>
                  )}
                </div>
              </div>
              <div className="recent-activity-item-score">
                {item.score !== null ? (
                  <span className={`recent-activity-score-value ${item.score >= 80 ? 'good' : item.score >= 60 ? 'warning' : 'poor'}`}>
                    {item.score}%
                  </span>
                ) : (
                  <span className="recent-activity-score-value empty">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default RecentActivity;

